import React, { useState, useEffect, useRef } from 'react';
import { Info, X, FileText, Captions, Settings, User, Target, Languages, Cpu, RectangleHorizontal, Image, Timer, Video, ChevronsUpDown, Building } from 'lucide-react';

const Scripting = ({ formData, setFormData, setIsLoading }) => {
  const { scenes } = formData;

  // State for UI options
  const [industries, setIndustries] = useState([]);
  const [videoLabelingOptions, setVideoLabelingOptions] = useState([]);
  const [scriptTemplateOptions, setScriptTemplateOptions] = useState([]);
  const [areOptionsLoading, setAreOptionsLoading] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const leaveTimeoutRef = useRef(null);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  
  // Version check - added at 2:19 PM for debugging
  console.log('Scripting component loaded - Version: 2.19PM');
  console.log('API Base URL:', apiBaseUrl);

  // Effect 1
  useEffect(() => {
    const fetchIndustries = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${apiBaseUrl}/api/public/industries`);
        if (!response.ok) throw new Error('Failed to fetch industries');
        const data = await response.json();
        const industriesList = Array.isArray(data) ? data : data.root;
        setIndustries(industriesList);

        const defaultConfigResponse = await fetch(`${apiBaseUrl}/api/public/settings`);
        if (!defaultConfigResponse.ok) throw new Error('Failed to fetch settings');
        const defaultConfig = await defaultConfigResponse.json();

        const defaultIndustry = industriesList.find(ind => ind.id === defaultConfig.default_industry_id);

        if (!formData.industry) {
            if (defaultIndustry) {
                setFormData(prev => ({ ...prev, industry: defaultIndustry.id }));
            } else if (industriesList.length > 0) {
                setFormData(prev => ({ ...prev, industry: industriesList[0].id }));
            }
        }
      } catch (error) {
        console.error('Error fetching industries:', error);
        setIsLoading(false); 
      }
    };
    fetchIndustries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]); 

  // Effect 2
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchDependentData = async () => {
      if (!formData.industry) {
        setScriptTemplateOptions([]);
        setVideoLabelingOptions([]);
        setIsLoading(false);
        return;
      }

      setAreOptionsLoading(true);
      setScriptTemplateOptions([]);
      setVideoLabelingOptions([]);

      try {
        const [templatesResponse, reportsResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/api/public/scene-template-names?industry=${formData.industry}`, { signal }),
          fetch(`${apiBaseUrl}/api/public/video-labeling-report-ids?industry=${formData.industry}`, { signal }),
        ]);

        if (!templatesResponse.ok || !reportsResponse.ok) {
          throw new Error('Failed to fetch industry-specific data');
        }

        const templatesData = await templatesResponse.json();
        const reportsData = await reportsResponse.json();

        setScriptTemplateOptions(templatesData);
        setVideoLabelingOptions(reportsData);

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Error fetching data for industry ${formData.industry}:`, error);
        }
      } finally {
        setAreOptionsLoading(false);
        setIsLoading(false);
      }
    };

    fetchDependentData();

    return () => {
      controller.abort();
    };
  }, [formData.industry, apiBaseUrl, setIsLoading]);

  const sampleData = {
    industry: 'GAMING',
    account: 'Learnings.AI',
    businessEntity: 'Vita Mahjong',
    description: 'Puzzle Game, Tile Matching, offers large tiles and a user-friendly interface compatible with pads and phones. a relaxing yet mentally engaging gaming experience, particularly focused on older adults.',
    targetMarket: 'Global',
    videoLanguage: 'English',
    scenes: [{
      description: `An older woman finds a moment of quiet joy and mental engagement while playing a user-friendly tile-matching game on her tablet in a comfortable, sunlit living room.`,
      imageUrl: null,
      gcsImageUrl: null,
      length: '8s',
      videoLabeling: '',
      scriptTemplate: 'Gaming'
    }]
  };

  const handleFillWithSampleData = () => {
    setFormData(prev => ({
      ...prev,
      ...sampleData,
    }));
    setIsTooltipVisible(false);
  };

  const handleIndustryChange = (e) => {
    const newIndustry = e.target.value;
    const resetScenes = scenes.map(scene => {
      const isLabelingMode = scene.scriptTemplate === 'VIDEO_LABELING';
      return {
        ...scene,
        videoLabeling: '',
        scriptTemplate: isLabelingMode ? 'VIDEO_LABELING' : ''
      };
    });

    setFormData(prev => ({
      ...prev,
      industry: newIndustry,
      scenes: resetScenes
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleModeChange = (index, newMode) => {
    const updatedScenes = scenes.map((scene, i) => {
        if (i === index) {
            const newScriptTemplate = newMode === 'Video Labeling' ? 'VIDEO_LABELING' : '';
            return {
              ...scene,
              scriptTemplate: newScriptTemplate,
              videoLabeling: '' 
            };
        }
        return scene;
    });
    setFormData(prev => ({ ...prev, scenes: updatedScenes }));
  };

  const handleSceneChange = async (index, e) => {
    const { name, value, files } = e.target;
    if (name === 'imageUrl' && files.length > 0) {
      const file = files[0];
      if (!file) return;
      const apiFormData = new FormData();
      apiFormData.append('file', file);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiBaseUrl}/api/upload-image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: apiFormData,
        });
        if (response.ok) {
          const data = await response.json();
          const updatedScenes = scenes.map((scene, i) => i === index ? { ...scene, imageUrl: data.displayImageUrl, gcsImageUrl: data.gcsImageUrl } : scene);
          setFormData(prev => ({ ...prev, scenes: updatedScenes }));
          alert('Image uploaded successfully!');
        } else {
          alert('Failed to upload image.');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
      }
      return;
    }

    // const updatedScenes = scenes.map((scene, i) => i === index ? { ...scene, [name]: value } : scene);
    const updatedScenes = scenes.map((scene, i) => {
      if (i === index) {
        const newScene = { ...scene, [name]: value };
        if (name === 'videoLabeling' && value !== '') {
          newScene.scriptTemplate = 'VIDEO_LABELING';
        } else if (name === 'scriptTemplate' && value !== 'VIDEO_LABELING') {
          newScene.videoLabeling = '';
        }
        return newScene;
      }
      return scene;
    });
    setFormData(prev => ({ ...prev, scenes: updatedScenes }));
  };

  const handleAddScene = () => {
    setFormData(prev => ({
      ...prev,
      scenes: [...prev.scenes, {
        description: '', imageUrl: null, gcsImageUrl: null, length: '8s', videoLabeling: '', scriptTemplate: '' 
      }]
    }));
  };

  const handleRemoveScene = (index) => {
    const updatedScenes = scenes.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, scenes: updatedScenes }));
  };

  const handleMouseEnter = () => {
    clearTimeout(leaveTimeoutRef.current);
    setIsTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => setIsTooltipVisible(false), 200);
  };

  const isIndustrySelected = formData.industry && formData.industry !== '';

  return (
    <div className="p-8 bg-white">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <FileText className="mr-2" />
          Tell us what and where you want to promote ?
        </h1>
        <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <button className="text-gray-500 hover:text-gray-700 p-2"><Info className="w-5 h-5" /></button>
          {isTooltipVisible && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-10 p-3">
              <p className="text-sm text-gray-600">Click to fill in sample values.</p>
              <button onClick={handleFillWithSampleData} className="mt-2 text-sm font-medium text-blue-600 hover:underline">Example: Vita Mahjong</button>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Building className="mr-2" />Industry</label>
              <select name="industry" value={formData.industry} onChange={handleIndustryChange} className="w-full border border-gray-300 rounded-md p-2">
                <option value="">-- Please select an industry (initial setup may take a moment...) --</option>
                {industries.map(ind => (<option key={ind.id} value={ind.id}>{ind.display_name}</option>))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Want to add your Industry to VeoMate? Contact <a href="mailto:veomate-dev@google.com" className="text-blue-500 hover:underline" target="_blank"  rel="noopener noreferrer">veomate-dev@</a> for one-click registration.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><User className="mr-2" />Account</label>
              <input type="text" name="account" value={formData.account} onChange={handleInputChange} placeholder="e.g. Leanrings.Al" className="w-full border border-gray-300 rounded-md p-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">Business Entity <Info className="ml-1 w-4 h-4" /></label>
              <input type="text" name="businessEntity" value={formData.businessEntity} onChange={handleInputChange} placeholder="e.g. Vita Mahjong" className="w-full border border-gray-300 rounded-md p-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><FileText className="mr-2" />Description</label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Puzzle Game, Tile Matching,..." className="w-full border border-gray-300 rounded-md p-2 h-24"></textarea>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Target className="mr-2" />Target Market</label>
                <input type="text" name="targetMarket" value={formData.targetMarket} onChange={handleInputChange} placeholder="e.g. Global" className="w-full border border-gray-300 rounded-md p-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Languages className="mr-2" />Video Language</label>
                <input type="text" name="videoLanguage" value={formData.videoLanguage} onChange={handleInputChange} placeholder="e.g. English" className="w-full border border-gray-300 rounded-md p-2" />
              </div>
            </div>
          </div>
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center"><Settings className="mr-2" />Additional Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Cpu className="mr-2" />Generation Model</label>
                <select name="generationModel" value={formData.generationModel} onChange={handleInputChange} className="w-full border border-gray-300 rounded-md p-2">
                  <option>veo-3.0-generate-001</option>
                  <option>veo-3.0-generate-preview</option>
                  <option>veo-3.0-fast-generate-001</option>
                  <option>veo-2.0-generate-exp</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><RectangleHorizontal className="mr-2" />Aspect Ratio</label>
                <select name="aspectRatio" value={formData.aspectRatio} onChange={handleInputChange} className="w-full border border-gray-300 rounded-md p-2">
                  <option>16:9</option>
                  <option>9:16</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div>
          {scenes.map((scene, index) => {
            const currentMode = scene.scriptTemplate === 'VIDEO_LABELING' ? 'Video Labeling' : 'Template';
            return (
            <div key={index} className="border border-gray-300 rounded-lg p-6 mb-4 relative">
              <button onClick={() => handleRemoveScene(index)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              <span className="absolute -top-3 left-4 bg-white px-2 text-sm font-medium text-gray-600 flex items-center"><Captions className="mr-1" />Scene</span>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">Scene Description <Info className="ml-1 w-4 h-4" /></label>
                  <textarea name="description" value={scene.description} onChange={(e) => handleSceneChange(index, e)} placeholder="e.g. this is a screenshot of our product..." className="w-full border border-gray-300 rounded-md p-2 h-20"></textarea>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Image className="mr-2" />Reference Image</label>
                    <input type="file" name="imageUrl" onChange={(e) => handleSceneChange(index, e)} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Timer className="mr-2" />Video Length (s)</label>
                    <select name="length" value={scene.length} onChange={(e) => handleSceneChange(index, e)} className="w-full border border-gray-300 rounded-md p-2"><option>8s</option></select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><ChevronsUpDown className="mr-2" />by Video Labeling or by Template?</label>
                  <select
                    value={currentMode}
                    onChange={(e) => handleModeChange(index, e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2"
                    disabled={!isIndustrySelected}
                  >
                    <option>Template</option>
                    <option>Video Labeling</option>
                  </select>
                </div>
                {currentMode === 'Video Labeling' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Video className="mr-2" />Refer to Video Labeling Results?</label>
                    <select
                      name="videoLabeling"
                      value={scene.videoLabeling}
                      onChange={(e) => handleSceneChange(index, e)}
                      className="w-full border border-gray-300 rounded-md p-2 disabled:bg-gray-100"
                      disabled={!isIndustrySelected || areOptionsLoading}
                    >
                      <option value="">
                        {areOptionsLoading ? 'Loading reports...' : (isIndustrySelected ? 'Select a report' : 'Select an industry first')}
                      </option>
                      {videoLabelingOptions.map((option) => (<option key={option} value={option}>{option}</option>))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Can't find the report you need? Contact gTech and submit your labeling request first.</p>
                  </div>
                )}
                {currentMode === 'Template' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><FileText className="mr-2" />Use a Script Template?</label>
                    <select
                      name="scriptTemplate"
                      value={scene.scriptTemplate}
                      onChange={(e) => handleSceneChange(index, e)}
                      className="w-full border border-gray-300 rounded-md p-2 disabled:bg-gray-100"
                      disabled={!isIndustrySelected || areOptionsLoading}
                    >
                      <option value="">
                        {areOptionsLoading ? 'Loading templates...' : (isIndustrySelected ? 'Select a template' : 'Select an industry first')}
                      </option>
                      {scriptTemplateOptions.map((option) => (<option key={option} value={option}>{option}</option>))}
                    </select>
                     <p className="text-xs text-gray-500 mt-1">
                      Can't find the template you need? Contact <a href="mailto:veomate-dev@google.com" className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">veomate-dev@</a> for one-click registration.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )})}
          <button onClick={handleAddScene} className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-full px-4 py-2">+ Add Another Scene</button>
        </div>
      </div>
    </div>
  );
};

export default Scripting;
